import React from 'react';
import { SlaVerdict } from '../../../interfaces/panel-data';
import { formatDateFromDate } from '../../../utils/date-helper';
import { formatDuration } from '../../../utils/format-duration';
import { VERDICT_LABELS } from './sla-verdict-labels';
import './DeltaStrip.scss';

export interface DeltaStripCell {
  id: string;
  createdAtMs: number;
  verdict: SlaVerdict;
}

/**
 * Seven verdicts onto the four band colours. Both overruns share the red, and all three kinds of
 * un-judged row share the grey, because none of them is a claim about compliance.
 */
const CELL_MODIFIERS: Record<SlaVerdict, string> = {
  WITHIN_SLA: 'within',
  AT_RISK: 'at-risk',
  BREACHING: 'breaching',
  BREACHED: 'breaching',
  NOT_REGISTERED: 'not-registered',
  STALE: 'not-registered',
  UNMEASURED: 'not-registered',
};

interface DeltaStripProps {
  cells: DeltaStripCell[];
  /** Rendered only when the cap withheld cells — see `isCapped`. */
  capNote: string | undefined;
  /** How many cells the backend was willing to send, which is what makes truncation detectable. */
  cap: number;
  clockExplanation: string | undefined;
}

/** Native `title` rather than a popover: there is one per delta, up to the cap of them on screen. */
const cellTitle = (cell: DeltaStripCell): string =>
  `${cell.id} · created ${formatDateFromDate(new Date(cell.createdAtMs))} · ${
    VERDICT_LABELS[cell.verdict]
  }`;

/**
 * One website's EPC deltas, one cell each, in creation order — the shape of the chain over time.
 *
 * The cells are sorted here because the aggregation returns them newest first, so that the cap drops the
 * oldest deltas rather than the newest. Drawn in the payload's order the strip would tell the story
 * backwards.
 *
 * Nothing is rendered without cells: an empty bordered box asserts that a website's chain is empty, which
 * is indistinguishable from one nobody has indexed yet.
 */
export const DeltaStrip: React.FC<DeltaStripProps> = ({ cells, capNote, cap, clockExplanation }) => {
  if (cells.length === 0) {
    return null;
  }

  const ordered = [...cells].sort((left, right) => left.createdAtMs - right.createdAtMs);
  const oldest = ordered[0];
  const newest = ordered[ordered.length - 1];
  // The cap limits what was sent, and there is no pre-cap total to compare against, so a full strip is
  // treated as a partial one. Proving truncation before saying so would mean never saying so.
  const isCapped = cells.length >= cap;

  return (
    <div className="ovp-dstrip">
      {clockExplanation && <p className="ovp-dstrip__note">{clockExplanation}</p>}

      <div
        className="ovp-dstrip__cells"
        role="img"
        aria-label={`${ordered.length.toLocaleString()} deltas in creation order, coloured by SLA verdict`}
        data-testid="sla-delta-strip"
      >
        {ordered.map((cell) => (
          <span
            key={cell.id}
            className={`ovp-dstrip__cell ovp-dstrip__cell--${CELL_MODIFIERS[cell.verdict]}`}
            title={cellTitle(cell)}
            data-testid={`delta-cell-${CELL_MODIFIERS[cell.verdict]}`}
          />
        ))}
      </div>

      {/* A strip of identical cells has no other way to say what span it covers, and the last 500 deltas
          are a very different window on a busy website than on a quiet one. */}
      <div className="ovp-dstrip__axis">
        <span>{`${oldest.id} · ${formatDateFromDate(new Date(oldest.createdAtMs))}`}</span>
        <span className="ovp-dstrip__axis-span">
          {`${ordered.length.toLocaleString()} deltas over ${formatDuration(
            Math.max(0, newest.createdAtMs - oldest.createdAtMs)
          )}`}
        </span>
        <span>{`${newest.id} · ${formatDateFromDate(new Date(newest.createdAtMs))}`}</span>
      </div>

      {isCapped && capNote && (
        <p className="ovp-dstrip__note" data-testid="sla-delta-strip-cap-note">
          {capNote}
        </p>
      )}
    </div>
  );
};
